import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as commonServices from "../../Services/common";

export interface MedicineItem {
  product_detail_id: number;
  medicine: number;
  batch_no: string;
  /** Purchase bill from `product_details` (inventory); optional for display */
  purchase_bill_no?: string | number | null;
  qty: number;
  unit_price: number;
  expiry_date: Date | string;
  cgst: number;
  sgst: number;
  gstType: string;
  total: number;
  date_of_service?: string;
  created_at?: string;
  order_product_id?: number; // For updating existing orders
}

interface ServiceItem {
  service: number;
  qty: number;
  unit_price: number;
  cgst: number;
  sgst: number;
  total: number;
  date_of_service?: string;
  created_at?: string;
  order_service_id?: number; // For updating existing orders
}

interface MedicineFormData {
  medicine: string;
  batch_no: string;
  qty: string;
  unit_price: string;
  expiry_date: Date | null;
  totalQty: number;
  product_detail_id: number;
  cgst: number;
  sgst: number;
  gstType: string;
  date_of_service?: string;
  allData: any[];
}

interface ServiceFormData {
  service: string;
  servicePrice: string;
  serviceQty: string;
  serviceDesc: string;
  date_of_service?: string;
}

interface CalculationsData {
  discountType: string;
  discountValue: string;
  paymentMode: string;
  amountReceived: string;
  comments: string;
  hospitalUnit: string;
  hospitalUnitName?: string;
  totalUnits: string;
  total: string;
  tax: string;
  subTotal: string;
  medicineSubTotal: string;
  serviceSubTotal: string;
  customSubTotal: string;
  discount: string;
  roundOff: string;
  grandTotal: string;
  amountPaid: string;
  dueAmount: string;
  hospitalDues: string;
  claimDues: string;
  fee: string;
  tpa_id?: string;
  billingMode: "SELF" | "TPA";
  claimAmount: string;
  isSeparateClaimBill?: boolean;
  tpaBalance?: string;
  order_id?: number | string | null;
  claim_order_id?: number | string | null;
  hospitalPaid?: string;
  claimPaid?: string;
  claimStatus?: string | null;
  tpaPaid?: string;
  copayPaid?: string;
  patientPaid?: string;
  patientPending?: string;
  tpaPending?: string;
  internalCost?: string;
}

interface CustomItem {
  customItemId?: number | string | null;
  name: string;
  qty: number;
  unit_price: number;
  total: number;
  date_of_service?: string;
  created_at?: string;
  order_custom_billing_item_id?: number;
  order_claim_item_id?: number;
}

interface CustomItemFormData {
  name: string;
  price: string;
  qty: string;
  date_of_service?: string;
}

interface BillingState {
  medicineForm: MedicineFormData;
  serviceForm: ServiceFormData;
  customForm: CustomItemFormData;
  calculations: CalculationsData;
  medicineItems: MedicineItem[];
  serviceItems: ServiceItem[];
  customItems: CustomItem[];
  claimItems: CustomItem[];
  lastTpaClaimItems: CustomItem[];
  claimForm: CustomItemFormData;
  visitId: string | null;
  patientId: string | null;
  billId: string | null;
  claimBillId: string | null;
  billNoInc: string | null;
  /** Case-3 internal statement ref ("{claim}-INT") when the hospital bill has no invoice number */
  hospitalRefNo: string | null;
  claimBillNoInc: string | null;
  ipdDetailsData?: any;
  isIPD: boolean;
  payments: any[];
  isCancelled: boolean;
  /** Primary bill `orders.status` when API exposes it; drives entity-first cancel detection */
  primaryOrderStatus?: string;
  cancelledBy?: any;
  cancelledAt?: string;
  revertedBy?: any;
  revertedAt?: string;
  cancelReason?: string;
  canCancelBill?: boolean;
  canRevertBill?: boolean;
  hospitalState?: string | null;
  patientState?: string | null;
}

export const initialState: BillingState = {
  medicineForm: {
    medicine: "",
    batch_no: "",
    qty: "",
    unit_price: "",
    expiry_date: null,
    totalQty: 0,
    product_detail_id: 0,
    cgst: 0,
    sgst: 0,
    gstType: "",
    allData: [],
  },
  serviceForm: {
    service: "",
    servicePrice: "",
    serviceQty: "",
    serviceDesc: "",
  },
  customForm: {
    name: "",
    price: "",
    qty: "1",
  },
  calculations: {
    discountType: "FLAT",
    discountValue: "0",
    paymentMode: "Cash",
    amountReceived: "0",
    comments: "",
    hospitalUnit: "",
    hospitalUnitName: "",
    totalUnits: "0",
    total: "0.00",
    tax: "0.00",
    subTotal: "0.00",
    medicineSubTotal: "0.00",
    serviceSubTotal: "0.00",
    customSubTotal: "0.00",
    discount: "0.00",
    roundOff: "0.00",
    grandTotal: "0.00",
    amountPaid: "0.00",
    dueAmount: "0.00",
    hospitalDues: "0.00",
    claimDues: "0.00",
    fee: "0",
    billingMode: "SELF",
    claimAmount: "0.00",
    isSeparateClaimBill: true,
    tpaBalance: "0.00",
    order_id: null,
    claim_order_id: null,
    hospitalPaid: "0.00",
    claimPaid: "0.00",
    tpaPaid: "0.00",
    copayPaid: "0.00",
    patientPaid: "0.00",
    patientPending: "0.00",
    tpaPending: "0.00",
    internalCost: "0.00",
  },
  medicineItems: [],
  serviceItems: [],
  customItems: [],
  claimItems: [],
  lastTpaClaimItems: [],
  claimForm: {
    name: "",
    price: "",
    qty: "1",
  },
  visitId: null,
  patientId: null,
  billId: null,
  claimBillId: null,
  billNoInc: null,
  hospitalRefNo: null,
  claimBillNoInc: null,
  isIPD: false,
  payments: [],
  isCancelled: false,
  primaryOrderStatus: undefined,
  cancelledBy: null,
  cancelledAt: undefined,
  revertedBy: null,
  revertedAt: undefined,
  cancelReason: undefined,
  canCancelBill: false,
  canRevertBill: false,
  hospitalState: null,
  patientState: null,
};

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    // Medicine form actions
    updateMedicineForm: (state, action: PayloadAction<Partial<MedicineFormData>>) => {
      state.medicineForm = { ...state.medicineForm, ...action.payload };
    },
    clearMedicineForm: (state) => {
      state.medicineForm = {
        medicine: "",
        batch_no: "",
        qty: "",
        unit_price: "",
        expiry_date: null,
        totalQty: 0,
        product_detail_id: 0,
        cgst: 0,
        sgst: 0,
        gstType: "",
        allData: state.medicineForm.allData, // Keep allData
      };
    },

    // Service form actions
    updateServiceForm: (state, action: PayloadAction<Partial<ServiceFormData>>) => {
      state.serviceForm = { ...state.serviceForm, ...action.payload };
    },
    clearServiceForm: (state) => {
      state.serviceForm = {
        service: "",
        servicePrice: "",
        serviceQty: "",
        serviceDesc: "",
      };
    },

    // Custom item form actions
    updateCustomForm: (state, action: PayloadAction<Partial<CustomItemFormData>>) => {
      state.customForm = { ...state.customForm, ...action.payload };
    },
    clearCustomForm: (state) => {
      state.customForm = {
        name: "",
        price: "",
        qty: "1",
      };
    },

    // Claim item form actions
    updateClaimForm: (state, action: PayloadAction<Partial<CustomItemFormData>>) => {
      state.claimForm = { ...state.claimForm, ...action.payload };
    },
    clearClaimForm: (state) => {
      state.claimForm = {
        name: "",
        price: "",
        qty: "1",
      };
    },

    // Add items
    addMedicineItem: (state, action: PayloadAction<MedicineItem>) => {
      state.medicineItems.push(action.payload);
      // Reset discount when items are added
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    addServiceItem: (state, action: PayloadAction<ServiceItem>) => {
      state.serviceItems.push(action.payload);
      // Reset discount when items are added
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    addCustomItem: (state, action: PayloadAction<CustomItem>) => {
      state.customItems.push(action.payload);
      // Reset discount when items are added
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Add claim items
    addClaimItem: (state, action: PayloadAction<CustomItem>) => {
      state.claimItems.push(action.payload);
      state.lastTpaClaimItems = [...state.claimItems];
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Update items
    updateMedicineItem: (
      state,
      action: PayloadAction<{ index: number; qty?: number; date_of_service?: string }>,
    ) => {
      const item = state.medicineItems[action.payload.index];
      if (item) {
        if (action.payload.qty !== undefined) {
          item.qty = action.payload.qty;
          item.total = item.qty * item.unit_price;
        }
        if (action.payload.date_of_service !== undefined) {
          item.date_of_service = action.payload.date_of_service;
        }
        billingSlice.caseReducers.recalculateAmounts(state);
      }
    },
    updateServiceItem: (
      state,
      action: PayloadAction<{ index: number; qty?: number; date_of_service?: string }>,
    ) => {
      const item = state.serviceItems[action.payload.index];
      if (item) {
        if (action.payload.qty !== undefined) {
          item.qty = action.payload.qty;
          item.total = item.qty * item.unit_price;
        }
        if (action.payload.date_of_service !== undefined) {
          item.date_of_service = action.payload.date_of_service;
        }
        billingSlice.caseReducers.recalculateAmounts(state);
      }
    },
    updateCustomItem: (
      state,
      action: PayloadAction<{
        index: number;
        qty?: number;
        unit_price?: number;
        name?: string;
        date_of_service?: string;
      }>,
    ) => {
      const item = state.customItems[action.payload.index];
      if (item) {
        if (action.payload.qty !== undefined) item.qty = action.payload.qty;
        if (action.payload.unit_price !== undefined) item.unit_price = action.payload.unit_price;
        if (action.payload.name !== undefined) item.name = action.payload.name;
        if (action.payload.date_of_service !== undefined)
          item.date_of_service = action.payload.date_of_service;
        item.total = item.qty * item.unit_price;
        billingSlice.caseReducers.recalculateAmounts(state);
      }
    },
    updateClaimItem: (
      state,
      action: PayloadAction<{
        index: number;
        qty?: number;
        unit_price?: number;
        name?: string;
        date_of_service?: string;
      }>,
    ) => {
      const item = state.claimItems[action.payload.index];
      if (item) {
        if (action.payload.qty !== undefined) item.qty = action.payload.qty;
        if (action.payload.unit_price !== undefined) item.unit_price = action.payload.unit_price;
        if (action.payload.name !== undefined) item.name = action.payload.name;
        if (action.payload.date_of_service !== undefined)
          item.date_of_service = action.payload.date_of_service;
        item.total = item.qty * item.unit_price;
        state.lastTpaClaimItems = [...state.claimItems];
        billingSlice.caseReducers.recalculateAmounts(state);
      }
    },

    // Remove items
    removeMedicineItem: (state, action: PayloadAction<number>) => {
      state.medicineItems.splice(action.payload, 1);
      // Reset discount when items are deleted
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    removeServiceItem: (state, action: PayloadAction<number>) => {
      state.serviceItems.splice(action.payload, 1);
      // Reset discount when items are deleted
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    removeCustomItem: (state, action: PayloadAction<number>) => {
      state.customItems.splice(action.payload, 1);
      // Reset discount when items are deleted
      state.calculations.discountType = "FLAT";
      state.calculations.discountValue = "0";
      billingSlice.caseReducers.recalculateAmounts(state);
    },
    removeClaimItem: (state, action: PayloadAction<number>) => {
      state.claimItems.splice(action.payload, 1);
      state.lastTpaClaimItems = [...state.claimItems];
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Calculations actions
    updateCalculations: (state, action: PayloadAction<Partial<CalculationsData>>) => {
      const previousBillingMode = state.calculations.billingMode;
      state.calculations = { ...state.calculations, ...action.payload };

      // Preserve claim rows across mode toggles: TPA -> SELF -> TPA.
      // If user switches back to TPA and current claim list is empty, restore last known TPA claim rows.
      if (action.payload.billingMode) {
        const nextBillingMode = action.payload.billingMode;
        if (previousBillingMode === "TPA" && nextBillingMode === "SELF") {
          // Cache current TPA items before switching to SELF
          state.lastTpaClaimItems = [...state.claimItems];
          // Clear current claim items so they don't affect SELF mode calculations/UI
          state.claimItems = [];
        } else if (
          previousBillingMode === "SELF" &&
          nextBillingMode === "TPA" &&
          state.lastTpaClaimItems.length > 0
        ) {
          // Force restore from cache when switching back to TPA
          state.claimItems = [...state.lastTpaClaimItems];
        }
      }
      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Recalculate amounts
    recalculateAmounts: (state) => {
      // FORCE RECALCULATION: Ensure all item totals are strictly Price * Qty (Gross)
      // This self-corrects historical records saved with incorrect 'net' totals.
      state.medicineItems.forEach((item) => {
        item.total = item.qty * item.unit_price;
        // FORCE TAX RECOVERY: Even if saved as 0, lookup master data to show correct breakup
        if (item.medicine) {
          const details = commonServices.getMedicineById(item.medicine);
          if (details) {
            // Only overwrite if current tax is zero (to avoid overwriting intentional overrides if any)
            if (
              (!item.cgst || parseFloat(item.cgst.toString()) === 0) &&
              (!item.sgst || parseFloat(item.sgst.toString()) === 0)
            ) {
              item.cgst = details.cgst ?? details.cgst_paisa ?? (details.gst ? details.gst / 2 : 0);
              item.sgst = details.sgst ?? details.sgst_paisa ?? (details.gst ? details.gst / 2 : 0);
              item.gstType = details.gst_type ?? details.gstType ?? "INCLUSIVE";
            }
          }
        }
      });
      state.serviceItems.forEach((item) => {
        item.total = item.qty * item.unit_price;
      });
      state.customItems.forEach((item) => {
        item.total = item.qty * item.unit_price;
      });
      state.claimItems.forEach((item) => {
        item.total = item.qty * item.unit_price;
      });

      const data = commonServices.getAmountCalculatedForMedicineOrder({
        arr: state.medicineItems,
        serviceArr: state.serviceItems,
        // Hospital Sub Total should only include hospital-level items.
        // Claim-only items (claimItems) are tracked separately for the TPA breakdown.
        customArr: state.customItems,
        discountType: state.calculations.discountType,
        // User Request: When TPA: discount value should not come up bcz discount is for internal.
        // Force to 0 for TPA mode unless it's a saved order (though user says hide it even then for TPA breakdown)
        discountValue:
          state.calculations.billingMode === "TPA"
            ? 0
            : parseFloat(state.calculations.discountValue) || 0,
        fee: parseFloat(state.calculations.fee) || 0,
        isIpd: state.isIPD,
        ipdDetailsData: state.ipdDetailsData,
        hospState: state.hospitalState,
        patState: state.patientState,
      });

      state.calculations.totalUnits = data.totalUnits?.toString() || "0";
      state.calculations.total = data.total?.toFixed(2) || "0.00";
      state.calculations.tax = data.tax?.toFixed(2) || "0.00";
      // The bill's pre-discount subtotal is inclusive of bed charges, taxes, fee, and line items (data.total)
      state.calculations.subTotal = data.total?.toFixed(2) || "0.00";
      state.calculations.medicineSubTotal = data.medicineSubTotal?.toFixed(2) || "0.00";
      state.calculations.serviceSubTotal = data.serviceSubTotal?.toFixed(2) || "0.00";
      state.calculations.customSubTotal = data.customSubTotal?.toFixed(2) || "0.00";
      state.calculations.discount = data.discount?.toFixed(2) || "0.00";

      // Calculate round off: difference between rounded and actual grand total
      // roundOff = rounded_value - actual_value
      // If grandTotal is 100.48, roundOff = 100 - 100.48 = -0.48
      // If grandTotal is 99.52, roundOff = 100 - 99.52 = 0.48
      const actualGrandTotal = data.grandTotal || 0;
      // Standard Rounding: Round to nearest rupee
      const roundedGrandTotal = Math.round(actualGrandTotal);
      const roundOff = roundedGrandTotal - actualGrandTotal;
      state.calculations.roundOff = roundOff.toFixed(2);
      state.calculations.grandTotal = roundedGrandTotal.toFixed(2);

      // Recalculate claim total from individual claim items.
      // For separate-claim TPA flow, claim total must come only from claim bill lines.
      if (state.claimItems.length > 0) {
        const claimTotalPaisa = state.claimItems.reduce(
          (sum, item) => sum + Math.round(item.total * 100),
          0,
        );
        state.calculations.claimAmount = (claimTotalPaisa / 100).toFixed(2);
      } else if (
        state.calculations.billingMode === "TPA" &&
        state.calculations.isSeparateClaimBill === false
      ) {
        // Case 2: Unified Bill - No separate claim invoice
        state.calculations.claimAmount = "0.00";
      } else if (state.calculations.billingMode === "TPA") {
        state.calculations.claimAmount = "0.00";
      }

      state.calculations.internalCost = parseFloat(state.calculations.grandTotal).toFixed(2);

      // Calculate due amount based on the final grand total (after round off)
      const billingMode = state.calculations.billingMode;
      const isSeparateClaim = state.calculations.isSeparateClaimBill !== false;

      const hospitalOrderId = String(state.calculations.order_id || "");
      const claimOrderId = String(state.calculations.claim_order_id || "");

      // Group payments by order_id
      const hospitalPayments = state.payments.filter(
        (p) => String(p.order_id || "") === hospitalOrderId,
      );
      const claimPayments = state.payments.filter(
        (p) => String(p.order_id || "") === claimOrderId && claimOrderId !== "",
      );

      const hospitalPaid = hospitalPayments.reduce(
        (sum, p) => sum + parseFloat(String(p.amount || 0)),
        0,
      );
      const claimPaid = claimPayments.reduce(
        (sum, p) => sum + parseFloat(String(p.amount || 0)),
        0,
      );

      // Case 3 (Separate TPA): Insurance vs Copay vs Patient logic
      const claimInsurancePayments = claimPayments.filter((p) => p.payment_mode === "INSURANCE");
      const claimCopayPayments = claimPayments.filter((p) => p.payment_mode !== "INSURANCE");

      const claimInsurancePaid = claimInsurancePayments.reduce(
        (sum, p) => sum + parseFloat(String(p.amount || 0)),
        0,
      );
      const claimCopayPaid = claimCopayPayments.reduce(
        (sum, p) => sum + parseFloat(String(p.amount || 0)),
        0,
      );

      const amountReceived = parseFloat(state.calculations.amountReceived || "0") || 0;

      if (billingMode === "TPA" && isSeparateClaim) {
        // Case 3: Separate TPA - Strictly show Claim Account (C - I)
        const claimBackendTotal = parseFloat(state.calculations.claimAmount || "0") || 0;
        const totalClaimPaidByInsurance = claimInsurancePaid + amountReceived;

        const claimPaidStr = totalClaimPaidByInsurance.toFixed(2);
        const claimDuesStr = (
          claimBackendTotal -
          totalClaimPaidByInsurance -
          claimCopayPaid
        ).toFixed(2);

        state.calculations.claimDues = claimDuesStr;
        state.calculations.claimPaid = claimPaidStr;
        state.calculations.tpaPaid = claimPaidStr;
        state.calculations.copayPaid = claimCopayPaid.toFixed(2);
        state.calculations.tpaPending = claimDuesStr;

        // Active View totals focus on the INSURANCE portion
        state.calculations.dueAmount = claimDuesStr;
        state.calculations.amountPaid = claimPaidStr;
        state.calculations.tpaBalance = claimDuesStr;

        // Track hospital dues separately
        const hospitalBackendTotal = parseFloat(state.calculations.grandTotal || "0") || 0;
        state.calculations.hospitalDues = (hospitalBackendTotal - hospitalPaid).toFixed(2);
        state.calculations.hospitalPaid = hospitalPaid.toFixed(2);
        state.calculations.patientPaid = hospitalPaid.toFixed(2);
        state.calculations.patientPending = state.calculations.hospitalDues;
      } else {
        // Case 1 & 2: SELF or Unified TPA - Strictly show Hospital Account (H - P)
        const hospitalBackendTotal = parseFloat(state.calculations.grandTotal || "0") || 0;
        const totalHospitalPaid = hospitalPaid + amountReceived;

        const hospitalPaidStr = totalHospitalPaid.toFixed(2);
        const hospitalDuesStr = (hospitalBackendTotal - totalHospitalPaid).toFixed(2);

        state.calculations.hospitalDues = hospitalDuesStr;
        state.calculations.hospitalPaid = hospitalPaidStr;
        state.calculations.patientPaid = hospitalPaidStr;
        state.calculations.patientPending = hospitalDuesStr;

        // Active View totals
        state.calculations.dueAmount = hospitalDuesStr;
        state.calculations.amountPaid = hospitalPaidStr;

        // Track claim status separately
        state.calculations.claimDues = "0.00";
        state.calculations.claimPaid = claimPaid.toFixed(2);
        state.calculations.tpaPaid = "0.00";
        state.calculations.copayPaid = "0.00";
        state.calculations.tpaPending = "0.00";
        state.calculations.tpaBalance = "0.00";
      }
    },

    // Set billing context
    setBillingContext: (
      state,
      action: PayloadAction<{
        visitId: string;
        patientId: string;
        billId?: string;
        claimBillId?: string | null;
        billNoInc?: string | null;
        hospitalRefNo?: string | null;
        claimBillNoInc?: string | null;
        isIPD?: boolean;
        ipdDetailsData?: any;
        clearItems?: boolean;
        billingMode?: "SELF" | "TPA";
        isCancelled?: boolean;
        cancelledBy?: any;
        cancelledAt?: string;
        revertedBy?: any;
        revertedAt?: string;
        cancelReason?: string;
        primaryOrderStatus?: string | null;
        canCancelBill?: boolean;
        canRevertBill?: boolean;
        hospitalState?: string | null;
        patientState?: string | null;
      }>,
    ) => {
      // Clear items if visitId or patientId changes (switching between patients)
      const visitIdChanged = state.visitId !== action.payload.visitId;
      const patientIdChanged = state.patientId !== action.payload.patientId;

      if (action.payload.clearItems || visitIdChanged || patientIdChanged) {
        state.medicineItems = [];
        state.serviceItems = [];
        state.customItems = [];
        state.claimItems = [];
        state.lastTpaClaimItems = [];
        state.payments = [];
        // Reset payment-related calculations when switching patients
        state.calculations.amountPaid = "0.00";
        state.calculations.dueAmount = "0.00";
        state.calculations.hospitalDues = "0.00";
        state.calculations.claimDues = "0.00";
        state.calculations.amountReceived = "0";
        // Clear hospital unit when switching patients to show selection for new patient
        state.calculations.hospitalUnit = "";
        // Clear invoice number when switching patients - will be regenerated based on selected unit
        state.billNoInc = null;
        state.hospitalRefNo = null;
        // Reset cancelled status when switching patients
        state.isCancelled = false;
        state.primaryOrderStatus = undefined;
        // Reset billing mode to SELF for new patient unless provided
        state.calculations.billingMode = action.payload.billingMode || "SELF";
        state.calculations.claimAmount = "0.00";
        state.calculations.comments = "";
        state.calculations.discountType = "FLAT";
        state.calculations.discountValue = "0";
        state.calculations.paymentMode = "Cash";
        state.hospitalState = null;
        state.patientState = null;
      }

      state.visitId = action.payload.visitId;
      state.patientId = action.payload.patientId;
      state.billId = action.payload.billId || null;
      state.claimBillId = action.payload.claimBillId || null;
      state.billNoInc = action.payload.billNoInc || null;
      state.hospitalRefNo = action.payload.hospitalRefNo || null;
      state.claimBillNoInc = action.payload.claimBillNoInc || null;
      state.isIPD = action.payload.isIPD || false;
      state.ipdDetailsData = action.payload.ipdDetailsData;
      state.isCancelled = action.payload.isCancelled || false;
      if (action.payload.primaryOrderStatus !== undefined) {
        state.primaryOrderStatus = action.payload.primaryOrderStatus ?? undefined;
      }
      state.cancelledBy = action.payload.cancelledBy;
      state.cancelledAt = action.payload.cancelledAt;
      state.revertedBy = action.payload.revertedBy;
      state.revertedAt = action.payload.revertedAt;
      state.cancelReason = action.payload.cancelReason;
      state.canCancelBill = action.payload.canCancelBill || false;
      state.canRevertBill = action.payload.canRevertBill || false;

      if (action.payload.hospitalState !== undefined) {
        state.hospitalState = action.payload.hospitalState;
      }
      if (action.payload.patientState !== undefined) {
        state.patientState = action.payload.patientState;
      }

      if (action.payload.billingMode) {
        state.calculations.billingMode = action.payload.billingMode;
      }

      billingSlice.caseReducers.recalculateAmounts(state);
    },

    // Clear all billing data
    clearBillingData: (_state) => {
      return { ...initialState };
    },

    // Load billing data from API
    loadBillingData: (
      state,
      action: PayloadAction<{
        medicineItems?: MedicineItem[];
        serviceItems?: ServiceItem[];
        customItems?: CustomItem[];
        claimItems?: any[];
        calculations?: Partial<CalculationsData>;
        billNoInc?: string | null;
        hospitalRefNo?: string | null;
        claimBillNoInc?: string | null;
        billId?: string | null;
        claimBillId?: string | null;
        payments?: any[];
        clearItems?: boolean; // Flag to explicitly clear items
        isCancelled?: boolean; // Flag to indicate if bill is cancelled
        primaryOrderStatus?: string | null;
        canCancelBill?: boolean;
        canRevertBill?: boolean;
        cancelledBy?: any;
        cancelledAt?: string;
        revertedBy?: any;
        revertedAt?: string;
        cancelReason?: string;
      }>,
    ) => {
      // Clear or update items if explicitly requested or if new items are being loaded
      const shouldUpdateMedicineItems =
        action.payload.clearItems || action.payload.medicineItems !== undefined;
      const shouldUpdateServiceItems =
        action.payload.clearItems || action.payload.serviceItems !== undefined;

      if (shouldUpdateMedicineItems) {
        state.medicineItems = action.payload.medicineItems || [];
      }
      if (shouldUpdateServiceItems) {
        state.serviceItems = action.payload.serviceItems || [];
      }
      if (action.payload.customItems !== undefined || action.payload.clearItems) {
        state.customItems = action.payload.customItems || [];
      }
      if (action.payload.claimItems !== undefined || action.payload.clearItems) {
        state.claimItems = action.payload.claimItems || [];
        // Only update the restore cache if we are in TPA mode, so we don't overwrite it with empty data from SELF mode
        if (state.calculations.billingMode === "TPA") {
          state.lastTpaClaimItems = [...state.claimItems];
        }
      }

      // If clearing items, always reset payment-related data
      if (action.payload.clearItems) {
        state.medicineItems = [];
        state.serviceItems = [];
        state.customItems = [];
        state.claimItems = [];
        state.lastTpaClaimItems = [];
        state.payments = [];
        state.calculations.amountPaid = "0.00";
        state.calculations.dueAmount = "0.00";
        state.calculations.amountReceived = "0";
        state.calculations.claimAmount = "0.00";
        // Clear hospital unit when clearing items (new patient with no saved order)
        state.calculations.hospitalUnit = "";
        state.calculations.order_id = null;
        state.calculations.claim_order_id = null;
        state.calculations.hospitalPaid = "0.00";
        state.calculations.claimPaid = "0.00";
        state.calculations.comments = "";
        state.calculations.discountType = "FLAT";
        state.calculations.discountValue = "0";
        state.calculations.paymentMode = "Cash";
        // Clear invoice number - will be regenerated based on selected unit when saving
        state.billNoInc = null;
        state.hospitalRefNo = null;
        // Reset cancelled status when clearing items
        state.isCancelled = false;
        state.primaryOrderStatus = undefined;
      }

      // Update cancelled status if provided
      if (action.payload.isCancelled !== undefined) {
        state.isCancelled = action.payload.isCancelled;
      }
      if (action.payload.primaryOrderStatus !== undefined) {
        state.primaryOrderStatus = action.payload.primaryOrderStatus ?? undefined;
      }
      if (action.payload.canCancelBill !== undefined) {
        state.canCancelBill = action.payload.canCancelBill;
      }
      if (action.payload.canRevertBill !== undefined) {
        state.canRevertBill = action.payload.canRevertBill;
      }
      if (action.payload.cancelledBy !== undefined) {
        state.cancelledBy = action.payload.cancelledBy;
      }
      if (action.payload.cancelledAt !== undefined) {
        state.cancelledAt = action.payload.cancelledAt;
      }
      if (action.payload.revertedBy !== undefined) {
        state.revertedBy = action.payload.revertedBy;
      }
      if (action.payload.revertedAt !== undefined) {
        state.revertedAt = action.payload.revertedAt;
      }
      if (action.payload.cancelReason !== undefined) {
        state.cancelReason = action.payload.cancelReason;
      }

      if (action.payload.calculations) {
        state.calculations = {
          ...state.calculations,
          ...action.payload.calculations,
          billingMode: action.payload.calculations.billingMode || state.calculations.billingMode,
          claimAmount:
            action.payload.calculations.claimAmount !== undefined
              ? action.payload.calculations.claimAmount
              : state.calculations.claimAmount,
        };
      }
      if (action.payload.billNoInc !== undefined) {
        state.billNoInc = action.payload.billNoInc;
      }
      if (action.payload.hospitalRefNo !== undefined) {
        state.hospitalRefNo = action.payload.hospitalRefNo;
      }
      if (action.payload.claimBillNoInc !== undefined) {
        state.claimBillNoInc = action.payload.claimBillNoInc;
      }
      if (action.payload.billId !== undefined) {
        state.billId = action.payload.billId;
      }
      if (action.payload.claimBillId !== undefined) {
        state.claimBillId = action.payload.claimBillId;
      }
      // Only update payments if clearItems is false (to avoid overwriting the clear above)
      if (action.payload.payments !== undefined && !action.payload.clearItems) {
        state.payments = action.payload.payments || [];
      }
      // Recalculate amounts after loading data
      billingSlice.caseReducers.recalculateAmounts(state);
    },
  },
});

export const {
  updateMedicineForm,
  clearMedicineForm,
  updateServiceForm,
  clearServiceForm,
  addMedicineItem,
  addServiceItem,
  updateMedicineItem,
  updateServiceItem,
  updateCustomItem,
  removeMedicineItem,
  removeServiceItem,
  removeCustomItem,
  updateCalculations,
  recalculateAmounts,
  setBillingContext,
  clearBillingData,
  loadBillingData,
  updateCustomForm,
  clearCustomForm,
  addCustomItem,
  updateClaimForm,
  clearClaimForm,
  addClaimItem,
  updateClaimItem,
  removeClaimItem,
} = billingSlice.actions;

export default billingSlice.reducer;
